/**
 * SelectionListPanel Component
 * Reusable left panel with color-coded selection list
 * Used in both Dashboard (tenant selection) and Tenant (policy selection) deploy dialogs
 */

import React from 'react';
import './ruleSelection.css';

interface SelectionListItem {
  id: string;
  name: string;
  subtitle: string;
  /** Deployment percentage (0-100) for color coding */
  deployPercent: number;
  /** Whether this item has validation errors (e.g., no rules selected) */
  hasError?: boolean;
  /** Optional label prefix to show beside the item name */
  prefix?: string;
}

interface SelectionListPanelProps {
  /** Header text for the panel */
  headerText: string;
  /** Array of items to display in the list */
  items: SelectionListItem[];
  /** ID of the currently active/selected item */
  activeId: string | null;
  /** Callback when an item is clicked */
  onItemClick: (id: string) => void;
}

/**
 * Determines status color based on deployment percentage
 * - Red (#d13438): 0% deployed
 * - Amber (#c74f08): 1-99% deployed (WCAG AA compliant - 4.62:1 contrast ratio)
 * - Green (#107c10): 100% deployed
 */
const getStatusColor = (deployPercent: number): string => {
  if (deployPercent === 0) return '#d13438';
  if (deployPercent >= 100) return '#107c10';
  return '#c74f08'; // Accessible warning orange - 4.62:1 contrast ratio (WCAG AA compliant)
};

/**
 * Renders a scrollable left panel with color-coded items
 * Color indicates deployment status (red → amber → green)
 */
export const SelectionListPanel: React.FC<SelectionListPanelProps> = ({
  headerText,
  items,
  activeId,
  onItemClick,
}) => {
  return (
    <div className="rsp-left-panel">
      <div className="rsp-panel-header">
        {headerText}
      </div>
      {items.map((item) => {
        const isActive = activeId === item.id;
        const statusColor = getStatusColor(item.deployPercent);

        return (
          <div
            key={item.id}
            onClick={() => onItemClick(item.id)}
            className={`rsp-list-item${isActive ? ' rsp-list-item--active' : ''}`}
            style={{ '--item-status-color': statusColor } as React.CSSProperties}
          >
            <div className="rsp-item-name" style={{ color: statusColor, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{item.name}</span>
              {item.prefix !== undefined && (
                <span
                  style={{
                    padding: '0 6px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    background: item.prefix ? '#eff6fc' : 'transparent',
                    color: item.prefix ? '#0078d4' : '#6b7280',
                    border: item.prefix ? '1px solid #0078d4' : '1px solid transparent',
                    whiteSpace: 'nowrap',
                  }}
                  title="Tenant label prefix"
                >
                  {item.prefix || '—'}
                </span>
              )}
            </div>
            <div className={`rsp-item-subtitle${item.hasError ? ' rsp-item-subtitle--error' : ''}`}>
              {item.subtitle}
              {item.hasError && ' ⚠'}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SelectionListPanel;
